<template>
    <div class="dropdown context-menu">
        <!-- One quiet pill for operating scope. Location and department are
             set once per shift and then left alone. The active user is kept
             visible beside this menu in the app top bar. -->
        <button
            type="button"
            class="context-menu__trigger"
            data-bs-toggle="dropdown"
            data-bs-auto-close="outside"
            aria-expanded="false"
            :aria-label="`Application scope: ${summary}`"
            data-cy="context-menu-trigger"
        >
            <i class="fas fa-map-marker-alt context-menu__icon" aria-hidden="true"></i>
            <span class="context-menu__summary">{{ summary }}</span>
            <i class="fas fa-chevron-down context-menu__caret" aria-hidden="true"></i>
        </button>

        <div class="dropdown-menu context-menu__panel">
            <div v-if="showLocation" class="context-menu__row">
                <span class="context-menu__label">Location</span>
                <LocationSwitcher
                    :use-custom-dropdown="false"
                    inline-label="Application"
                    inline-icon="building"
                />
            </div>

            <div v-if="showDepartment" class="context-menu__row">
                <span class="context-menu__label">Department</span>
                <DepartmentSwitcher
                    :use-custom-dropdown="false"
                    inline-label="Department"
                    inline-icon="sitemap"
                />
            </div>
        </div>
    </div>
</template>

<script>
import LocationSwitcher from "@/components/LocationSwitcher.vue";
import DepartmentSwitcher from "@/components/DepartmentSwitcher.vue";

export default {
    name: "ContextMenu",
    components: { LocationSwitcher, DepartmentSwitcher },
    props: {
        showLocation: { type: Boolean, default: false },
        showDepartment: { type: Boolean, default: false }
    },
    computed: {
        /**
         * "Main Location · Back of House". The active user is a separate,
         * visible control beside this scope summary in the app top bar.
         */
        summary() {
            const parts = [];
            if (this.showLocation) parts.push(this.$store.state.location?.name);
            if (this.showDepartment) parts.push(this.$store.state.department?.name);
            const text = parts.filter(Boolean).join(" · ");
            return text || "Set context";
        }
    }
};
</script>

<style scoped>
.context-menu {
    min-width: 0;
}

.context-menu__trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    max-width: 100%;
    min-height: 2rem;
    padding: 0 0.7rem;
    color: var(--forkto-ink-2);
    font-size: 0.8125rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 999px;
}

.context-menu__trigger:hover,
.context-menu__trigger[aria-expanded="true"] {
    color: var(--forkto-ink-1);
    background: var(--forkto-surface);
    border-color: var(--forkto-line);
}

.context-menu__trigger:focus-visible {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: 1px;
}

.context-menu__icon {
    flex: 0 0 auto;
    color: var(--forkto-ink-2);
    font-size: 0.75rem;
}

.context-menu__summary {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.context-menu__caret {
    flex: 0 0 auto;
    color: var(--forkto-ink-3);
    font-size: 0.625rem;
}

.context-menu__panel {
    min-width: 17rem;
    margin-top: 0.375rem;
    padding: 0.5rem;
    background: var(--forkto-surface);
    border: 1px solid var(--forkto-line);
    border-radius: 12px;
    box-shadow: 0 10px 28px rgba(var(--forkto-shadow-rgb), 0.12);
}

.context-menu__row {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.375rem 0.25rem;
}

.context-menu__row + .context-menu__row {
    border-top: 1px solid var(--forkto-line);
}

.context-menu__label {
    color: var(--forkto-ink-3);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

/* The switchers render in their inline mode. The shared page-header select
   caps itself at 220px for use in a header row; inside this panel it must
   fill the row or the right gutter ends up wider than the left. */
.context-menu__row :deep(.page-header-select) {
    width: 100%;
    max-width: none;
}

.context-menu__row :deep(.page-header-select__input.form-select) {
    width: 100%;
}
</style>
